/* eslint-disable */
import React, { useState, useMemo } from 'react';

// ╔══════════════════════════════════════════════════════════════════════╗
// ║  SCENARIO DATA — Replace this block to swap the entire scenario.   ║
// ║  UI code below this block does NOT need to change.                 ║
// ╚══════════════════════════════════════════════════════════════════════╝

const EVIDENCE_TYPES = {
  'note-gap': { label: 'Missing from visit note', icon: '⊘' },
  'protocol-gap': { label: 'Protocol step missing', icon: '△' },
  'time-expiring': { label: 'Deadline approaching', icon: '◷' },
  'state-mismatch': { label: 'Needs update', icon: '≠' },
  'cadence-drift': { label: 'Overdue', icon: '↝' },
  'gate-blocked': { label: 'Blocker', icon: '⊗' },
  'safety-trigger': { label: 'Safety check', icon: '!' },
  'transition-trigger': { label: 'Transition step', icon: '⇥' },
};

const PATIENTS = [
  {
    id: 'P001',
    name: 'Sarah Mitchell',
    age: 71,
    mrn: 'MRN-6120',
    athenaId: '612000',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Patel',
  },
  {
    id: 'P002',
    name: 'David Kowalski',
    age: 74,
    mrn: 'MRN-8835',
    athenaId: '883500',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Nakamura',
  },
  {
    id: 'P003',
    name: 'Linda Okonkwo',
    age: 68,
    mrn: 'MRN-4471',
    athenaId: '447100',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Chen',
  },
  {
    id: 'P004',
    name: 'Richard Huang',
    age: 73,
    mrn: 'MRN-2209',
    athenaId: '220900',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Patel',
  },
  {
    id: 'P005',
    name: 'Barbara Torres',
    age: 70,
    mrn: 'MRN-5587',
    athenaId: '558700',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Nakamura',
  },
  {
    id: 'P006',
    name: 'George Adams',
    age: 76,
    mrn: 'MRN-3344',
    athenaId: '334400',
    phase: 1,
    pathway: 'cognitive',
    clinician: 'Dr. Chen',
  },
];

const VISIT_TYPES = {
  99205: {
    label: 'New Patient Visit (99205)',
    short: '99205',
    pathway: 'cognitive',
    phase: 1,
    readinessDimensions: ['scheduling', 'stakeholders'],
    description: 'First visit — intake, history, and initial plan',
  },
  99483: {
    label: 'Cognitive Assessment (99483)',
    short: '99483',
    pathway: 'cognitive',
    phase: 2,
    readinessDimensions: ['evidence', 'assessments', 'scheduling', 'stakeholders'],
    description: 'Full cognitive workup — requires labs, MRI, Cryos, and caregiver present',
  },
};

const READINESS_DIMS = {
  evidence: {
    label: 'Test Results',
    icon: '◈',
    description: 'Labs, MRI, and biomarker results are back',
  },
  assessments: {
    label: 'Assessments',
    icon: '◇',
    description: 'Cognitive testing and caregiver questionnaires completed',
  },
  scheduling: {
    label: 'Scheduling',
    icon: '▫',
    description: 'Visit booked with the right type and timing',
  },
  stakeholders: {
    label: 'People',
    icon: '◎',
    description: 'Caregiver and family confirmed to attend',
  },
};

// Today's simulated date
const SIM_TODAY = '2026-02-03';

const TASKS = [
  // ═══ PATIENT: Sarah Mitchell (P001) — 99205 signed TODAY, nothing done ═══

  // Half A: Immediate gap closure (same-day)
  {
    id: 'T001',
    patientId: 'P001',
    team: 'clinical',
    title: 'Track dementia lab results — placed during visit',
    caption:
      'Labs (B12, TSH, CMP, CBC) were ordered by Dr. Patel during the 99205. Linked to readiness tracking — awaiting results.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-14',
    priority: 'medium',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'state-mismatch',
      summary:
        "Dementia labs (B12, TSH, CMP, CBC) were correctly ordered during the visit — confirmed in Athena orders. Linked to Phase 2 readiness tracking automatically. Expected turnaround: 5–7 days. No action needed now — system will flag if results don't arrive by 2/10.",
      source: 'Athena orders — 99205 encounter 2/3/2026',
      sourceRef: 'ORD-20260203-P001-LABS',
    },
  },
  {
    id: 'T002',
    patientId: 'P001',
    team: 'ops',
    title: 'Get the baseline MRI ordered',
    caption: "Note says 'MRI brain ordered' but no order or referral exists in Athena.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'note-gap',
      summary:
        "Dr. Patel's 99205 note says 'MRI brain ordered' but there's no imaging order or external referral in Athena. Needs to be placed today so results are back in time for the 99483.",
      source: 'Athena visit note — 99205 encounter 2/3/2026',
      sourceRef: 'ENC-20260203-P001',
    },
  },
  {
    id: 'T003',
    patientId: 'P001',
    team: 'clinical',
    title: 'Order blood biomarker panel (4-panel)',
    caption: 'Edge protocol: p-tau217, Aβ42/40, NfL, GFAP. 2026 target — ≥70% of Phase 1 patients.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-03',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'protocol-gap',
      summary:
        'Edge protocol calls for a 4-panel blood biomarker test at first visit. This is a 2026 MUST-HAVE target. Order needs to be placed today.',
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-BIO-001',
    },
  },
  {
    id: 'T004',
    patientId: 'P001',
    team: 'clinical',
    title: 'Order APOE genetic test',
    caption:
      'Tells us risk level for treatment side effects. Edge protocol wants this for all new patients.',
    role: 'MA',
    owner: 'Kim Torres',
    due: '2026-02-03',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'protocol-gap',
      summary:
        "APOE genotyping tells us the patient's genetic risk for ARIA if they go on treatment. Edge protocol wants this for all patients at first visit.",
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-APOE-001',
    },
  },
  {
    id: 'T005',
    patientId: 'P001',
    team: 'member',
    title: 'Complete new patient welcome call',
    caption: 'Finish paperwork collection, insurance verification, and caregiver identification.',
    role: 'Care Navigator',
    owner: 'Jordan Mills',
    due: '2026-02-04',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Post-99205 protocol requires a welcome call within 24 hours: verify insurance, collect outstanding paperwork, and identify the primary caregiver for CDR and visit accompaniment.',
      source: 'Post-99205 Orchestration — Half A',
      sourceRef: 'WF1-WELCOME-001',
    },
  },

  // Half A: Safety sub-process
  {
    id: 'T006',
    patientId: 'P001',
    team: 'ops',
    title: 'Create driving safety plan — patient agreed to stop',
    caption:
      "Note says 'patient agrees to stop driving.' Need verification task + caregiver education.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-05',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    evidence: {
      type: 'safety-trigger',
      summary:
        "Dr. Patel's note documents a driving safety discussion: 'Patient agrees to stop driving, daughter will take keys.' Safety sub-process requires: (1) key surrender verification task, (2) caregiver education module, (3) follow-up checkpoint at Phase 2 visit.",
      source: 'Post-99205 Safety Action Sub-Process',
      sourceRef: 'WF1-SAFE-DRIVE-001',
    },
  },

  // Half B: Phase 2 prerequisite chain
  {
    id: 'T007',
    patientId: 'P001',
    team: 'ops',
    title: 'Schedule Cryos cognitive testing session',
    caption: 'Must be done at least 2 weeks before the 99483 visit so results are ready.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-06',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Cryos 12-domain cognitive battery is a prerequisite for Phase 2. Must be scheduled within 3 days of 99205 and completed by week 4 so results are scored before the 99483.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CRYOS',
    },
  },
  {
    id: 'T008',
    patientId: 'P001',
    team: 'member',
    title: 'Send CDR questionnaire to caregiver',
    caption:
      'Caregiver must complete the Clinical Dementia Rating questionnaire before Phase 2 visit.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-07',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'CDR needs caregiver input. Must be sent within 3 days of identifying the primary caregiver and completed before the 99483 visit. Depends on welcome call (T005) to confirm caregiver contact.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CDR',
    },
  },
  {
    id: 'T009',
    patientId: 'P001',
    team: 'ops',
    title: 'Schedule 99483 visit (~6 weeks out)',
    caption: 'Must be booked as 99483 + 96132, not a generic follow-up. Within the 6-week window.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-10',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'The Phase 2 visit must be booked with the correct visit type (99483 + 96132). A common failure mode is booking a generic follow-up instead, which wastes the slot. Target: ~6 weeks from today.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-SCHED',
    },
  },

  // ═══ PATIENT: David Kowalski (P002) — 99205 signed 3 DAYS AGO, partial progress ═══
  {
    id: 'T010',
    patientId: 'P002',
    team: 'ops',
    title: 'Track MRI results — ordered 1/31, not yet resulted',
    caption: "MRI was ordered at outside facility. Report hasn't arrived in Athena yet.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-07',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'state-mismatch',
      summary:
        'MRI brain was ordered on 1/31 at an outside imaging center. The order exists in Athena but no report has arrived. External facility reports sometimes never make it into the chart — this is a top failure mode.',
      source: 'Post-99205 Orchestration — Result Tracking',
      sourceRef: 'WF1-TRACK-MRI',
    },
  },
  {
    id: 'T011',
    patientId: 'P002',
    team: 'ops',
    title: 'Schedule Cryos session (overdue — should have been done by 2/2)',
    caption: "Protocol says schedule within 3 days. We're now 1 day late.",
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "Cryos scheduling should happen within 3 days of the 99205 (by 2/2). It's now 2/3 and no booking exists. Escalation rule triggered: prerequisite overdue >1 day.",
      source: 'Post-99205 Orchestration — Escalation Rules',
      sourceRef: 'WF1-ESC-CRYOS',
    },
  },
  {
    id: 'T012',
    patientId: 'P002',
    team: 'member',
    title: 'Send CDR questionnaire to wife',
    caption: 'Wife identified as primary caregiver during welcome call. CDR not sent yet.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-04',
    priority: 'high',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Welcome call completed 2/1 — wife (Maria Kowalski) identified as primary caregiver. CDR instrument should have been sent within 24 hours of caregiver identification. Now 2 days late.',
      source: 'Post-99205 Orchestration — Half B Prerequisites',
      sourceRef: 'WF1-PREREQ-CDR',
    },
  },
  {
    id: 'T013',
    patientId: 'P002',
    team: 'ops',
    title: 'Schedule 99483 visit — correct type (99483 + 96132)',
    caption: 'Follow-up is booked but as a generic visit. Needs to be changed to 99483 + 96132.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-05',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'state-mismatch',
      summary:
        "A follow-up visit is scheduled for 3/12, but it's booked as a generic 99215 instead of 99483 + 96132. This is the #1 failure mode — wrong visit type means the cognitive assessment can't be billed or properly conducted.",
      source: 'Post-99205 Orchestration — Scheduling Reconciliation',
      sourceRef: 'WF1-SCHED-TYPE',
    },
  },

  // ═══ PATIENT: Linda Okonkwo (P003) — 99205 signed 1 WEEK AGO, most things done ═══
  {
    id: 'T014',
    patientId: 'P003',
    team: 'clinical',
    title: 'Record biological disease stage (ATN)',
    caption: 'Biomarker results are back. Dr. Chen needs to assign ATN stage before Phase 2.',
    role: 'Clinician',
    owner: 'Dr. Chen',
    due: '2026-02-07',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'edge',
    mustHave2026: true,
    evidence: {
      type: 'state-mismatch',
      summary:
        "Linda's biomarker results came back 2/1 (p-tau217 elevated, Aβ42/40 reduced). The ATN biological staging field in her record is still empty. Dr. Chen needs to assign it before Phase 2 so the care plan reflects the biology.",
      source: 'Mindspan Clinical Protocol — Edge Layer',
      sourceRef: 'EDGE-ATN-001',
    },
  },
  {
    id: 'T015',
    patientId: 'P003',
    team: 'member',
    title: 'Confirm son can attend 99483 visit on 2/24',
    caption:
      "Son is listed as primary caregiver. Day-before confirmation hasn't happened — visit is 3 weeks out.",
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-20',
    priority: 'medium',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'protocol-gap',
      summary:
        'Son (Michael Okonkwo) confirmed as primary caregiver. He needs to attend the 99483. Confirmation call is due at T-3 before the visit. This is on track but needs to be done.',
      source: 'Post-99205 Orchestration — Pre-Visit Gates',
      sourceRef: 'WF1-GATE-STAKEHOLDER',
    },
  },

  // ═══ PATIENT: Richard Huang (P004) — 99205 signed 2 WEEKS AGO, 99483 in 2 weeks ═══
  {
    id: 'T016',
    patientId: 'P004',
    team: 'ops',
    title: 'Chase lab results — drawn 1/22, still not resulted',
    caption: 'Dementia labs were drawn 12 days ago. Results should have been back in 5–7 days.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "Dementia labs (B12, TSH, CMP, CBC) were drawn on 1/22 — that's 12 days ago. Standard turnaround is 5–7 days. Results are >5 days overdue. Escalation rule: overdue >7 days → alert coordinator + ops lead.",
      source: 'Post-99205 Orchestration — Result Tracking Escalation',
      sourceRef: 'WF1-ESC-LABS',
    },
  },
  {
    id: 'T017',
    patientId: 'P004',
    team: 'member',
    title: 'Confirm wife can attend 99483 on 2/17',
    caption: 'T-14 readiness gate is today. Caregiver attendance must be confirmed.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-05',
    priority: 'high',
    status: 'in-progress',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'time-expiring',
      summary:
        "Richard's 99483 is on 2/17 — that's 14 days out. The T-14 pre-visit readiness sweep runs today. Caregiver (wife, Li-Mei Huang) must be confirmed for the visit. Contact was made 1/28 but no confirmation received.",
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-14',
      sourceRef: 'WF1-GATE-T14',
    },
  },

  // ═══ PATIENT: Barbara Torres (P005) — 99205 signed 4 WEEKS AGO, 99483 in 2 DAYS ═══
  {
    id: 'T018',
    patientId: 'P005',
    team: 'ops',
    title: 'URGENT: Cryos testing still not completed',
    caption:
      '99483 is in 2 days. Cryos was scheduled for 1/24 but patient no-showed. Not rescheduled.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'blocked',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'gate-blocked',
      summary:
        "Barbara's 99483 is on 2/5 (T-2). Cryos was scheduled for 1/24 but she no-showed. It was never rescheduled. Without Cryos results, the 99483 visit cannot proceed as designed. At T-2 with a RED dimension, the clinician must be alerted for a possible reschedule.",
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-2',
      sourceRef: 'WF1-GATE-T2-CRIT',
    },
  },
  {
    id: 'T019',
    patientId: 'P005',
    team: 'member',
    title: 'URGENT: CDR questionnaire not returned',
    caption: 'Sent to daughter on 1/10 — never completed. Visit is in 2 days.',
    role: 'Caregiver Liaison',
    owner: 'Dana Wright',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        'CDR instrument was sent to daughter (Rosa Torres) on 1/10 — 24 days ago. Never completed. The 99483 visit requires this. At T-2, this triggers a critical escalation: alert clinician, consider reschedule.',
      source: 'Post-99205 Orchestration — Pre-Visit Gate T-2',
      sourceRef: 'WF1-GATE-T2-CDR',
    },
  },
  {
    id: 'T020',
    patientId: 'P005',
    team: 'clinical',
    title: 'Review readiness — decide: proceed or reschedule 99483',
    caption: 'Two RED dimensions at T-2: Cryos not done, CDR not returned. Clinician must decide.',
    role: 'Clinician',
    owner: 'Dr. Nakamura',
    due: '2026-02-03',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'gate-blocked',
      summary:
        "Barbara's 99483 visit (2/5) has 2 RED readiness dimensions at the T-2 gate: Assessments (Cryos no-show, not rescheduled) and Stakeholders (CDR not returned). Protocol: if any dimension is RED at T-2, alert clinician for reschedule decision. Labs and MRI are green.",
      source: 'Post-99205 Orchestration — T-2 Escalation to Clinician',
      sourceRef: 'WF1-ESC-T2-CLIN',
    },
  },

  // ═══ PATIENT: George Adams (P006) — 99205 signed 6 WEEKS AGO, stalled ═══
  {
    id: 'T021',
    patientId: 'P006',
    team: 'ops',
    title: 'CRITICAL DRIFT: Patient stalled 42 days in Phase 1',
    caption: 'No 99483 scheduled, MRI not done, Cryos not scheduled. 6-week window expiring.',
    role: 'Coordinator',
    owner: 'Alex Reyes',
    due: '2026-02-03',
    priority: 'critical',
    status: 'flagged',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        "George's 99205 was on 12/23/2025 — 42 days ago. The 6-week window for Phase 2 conversion is now expiring. Status: labs resulted (12/30), but MRI never ordered, Cryos never scheduled, 99483 never booked. This triggers a CRITICAL drift alert to clinician + ops lead.",
      source: 'Post-99205 Orchestration — Phase 1 Stall Detection',
      sourceRef: 'WF1-DRIFT-42DAY',
    },
  },
  {
    id: 'T022',
    patientId: 'P006',
    team: 'clinical',
    title: 'Review stalled patient — determine next steps',
    caption:
      'Patient dropped out of the pipeline for 6 weeks. Was the 99483 path abandoned or just lost?',
    role: 'Clinician',
    owner: 'Dr. Chen',
    due: '2026-02-04',
    priority: 'critical',
    status: 'pending',
    pathway: 'cognitive',
    protocol: 'core',
    evidence: {
      type: 'cadence-drift',
      summary:
        'George has been in Phase 1 for 42 days with minimal progress. Dr. Chen needs to determine: was the 99483 pathway intentionally deferred (patient choice, comorbidity)? Or did this patient simply get lost in the handoff? If lost, immediate remediation is needed.',
      source: 'Post-99205 Orchestration — Drift Escalation to Clinician',
      sourceRef: 'WF1-DRIFT-CLIN',
    },
  },
];

const APPOINTMENTS = [
  // Barbara Torres — 99483 in 2 days (crisis)
  {
    id: 'A001',
    patientId: 'P005',
    date: '2026-02-05',
    time: '09:00',
    visitType: '99483',
    clinician: 'Dr. Nakamura',
    location: 'Clinic Room 1',
    readiness: {
      evidence: {
        status: 'green',
        detail:
          'All test results are back: dementia labs resulted 1/15, MRI resulted 1/20. Biomarkers resulted 1/25 (Edge). APOE: e3/e3.',
      },
      assessments: {
        status: 'red',
        detail:
          'Cryos not completed — patient no-showed 1/24 appointment, never rescheduled. CDR questionnaire sent to daughter 1/10, never returned.',
        tasks: ['T018', 'T019'],
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'red',
        detail:
          "Daughter (Rosa Torres) hasn't confirmed attendance or completed CDR. No contact since 1/10.",
        tasks: ['T019'],
      },
    },
  },
  // Richard Huang — 99483 in 2 weeks (at risk)
  {
    id: 'A002',
    patientId: 'P004',
    date: '2026-02-17',
    time: '14:00',
    visitType: '99483',
    clinician: 'Dr. Patel',
    location: 'Clinic Room 2',
    readiness: {
      evidence: {
        status: 'red',
        detail:
          'Dementia labs drawn 1/22 but results still not back (12 days — 5+ days overdue). MRI resulted 1/28, clear. Biomarkers resulted 1/30 (Edge).',
        tasks: ['T016'],
      },
      assessments: {
        status: 'green',
        detail: 'Cryos completed 1/29. CDR completed by wife 1/31. MoCA from 1/20: 24.',
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'yellow',
        detail:
          'Wife contacted 1/28, verbal confirmation but not formally confirmed. T-14 gate check is today.',
        tasks: ['T017'],
      },
    },
  },
  // Linda Okonkwo — 99483 in 3 weeks (READY)
  {
    id: 'A003',
    patientId: 'P003',
    date: '2026-02-24',
    time: '10:00',
    visitType: '99483',
    clinician: 'Dr. Chen',
    location: 'Clinic Room 3',
    readiness: {
      evidence: {
        status: 'green',
        detail:
          'All results back: labs resulted 2/1, MRI resulted 1/31. Biomarkers resulted 2/1 (p-tau217 elevated). APOE: e3/e4. ATN stage recorded by Dr. Chen on 2/2.',
      },
      assessments: {
        status: 'green',
        detail: 'Cryos completed 1/30. CDR completed by son 2/1. MoCA from 1/27: 22.',
      },
      scheduling: {
        status: 'green',
        detail: '99483 + 96132 booked correctly. Within the 6-week window.',
      },
      stakeholders: {
        status: 'green',
        detail: 'Son (Michael Okonkwo) confirmed attendance for 2/24. Contact verified 2/2.',
      },
    },
  },
  // David Kowalski — 99483 in 5 weeks (early, issues building)
  {
    id: 'A004',
    patientId: 'P002',
    date: '2026-03-12',
    time: '11:00',
    visitType: '99483',
    clinician: 'Dr. Nakamura',
    location: 'Clinic Room 1',
    readiness: {
      evidence: {
        status: 'yellow',
        detail:
          'Dementia labs resulted 2/2. MRI ordered 1/31 at outside facility — report not received yet. Biomarkers ordered 1/31 (Edge). APOE ordered 1/31 (Edge).',
        tasks: ['T010'],
      },
      assessments: {
        status: 'red',
        detail: 'Cryos not scheduled (1 day overdue). CDR not sent to wife yet.',
        tasks: ['T011', 'T012'],
      },
      scheduling: {
        status: 'red',
        detail:
          'Visit is booked but as a generic 99215 — wrong visit type. Must be changed to 99483 + 96132.',
        tasks: ['T013'],
      },
      stakeholders: {
        status: 'yellow',
        detail:
          'Wife identified during welcome call. No visit confirmation yet (too early — visit is 5 weeks out).',
      },
    },
  },
  // Sarah Mitchell — 99483 not yet booked (just started today)
  {
    id: 'A005',
    patientId: 'P001',
    date: '2026-03-17',
    time: 'TBD',
    visitType: '99483',
    clinician: 'Dr. Patel',
    location: 'TBD',
    noteUrl: 'https://athenanet.athenahealth.com/1/chart/encounter/20260203-612000',
    readiness: {
      evidence: {
        status: 'yellow',
        detail:
          "Dementia labs (B12, TSH, CMP, CBC) placed during visit — linked to tracking, awaiting results. MRI: note says 'order MRI brain with dementia protocol' but no imaging order or referral exists in Athena (gap). Blood biomarkers and APOE genotyping not ordered — Edge protocol requires both at Phase 1 (not mentioned in note).",
        tasks: ['T002', 'T003', 'T004'],
      },
      assessments: {
        status: 'red',
        detail:
          'Cryos not scheduled. CDR not sent — caregiver not yet identified (welcome call pending).',
        tasks: ['T007', 'T008'],
      },
      scheduling: {
        status: 'red',
        detail:
          '99483 visit not yet booked. Needs to be scheduled as 99483 + 96132 within the 6-week window.',
        tasks: ['T009'],
      },
      stakeholders: {
        status: 'red',
        detail: 'Caregiver not yet identified. Welcome call (today/tomorrow) will establish this.',
        tasks: ['T005'],
      },
    },
  },
];

const pathwayLabel = {
  cognitive: 'Cognitive',
  kisunla: 'Kisunla',
  'leqembi-iv': 'Leqembi IV',
  'leqembi-sc': 'Leqembi SC',
};
const teamMeta = {
  member: { label: 'Member Experience', icon: '○' },
  ops: { label: 'Operations', icon: '◎' },
  clinical: { label: 'Clinical', icon: '●' },
};

// ╔══════════════════════════════════════════════════════════════════════╗
// ║  END SCENARIO DATA — UI code below is scenario-independent.        ║
// ╚══════════════════════════════════════════════════════════════════════╝

// ─── HELPERS ───
const getPatient = (id) => PATIENTS.find((p) => p.id === id);
const getTask = (id) => TASKS.find((t) => t.id === id);
const athenaUrl = (id) => `https://athenanet.athenahealth.com/1/chart/patient/${id}`;
const daysUntil = (d) => Math.round((new Date(d) - new Date(SIM_TODAY)) / 86400000);
const formatDue = (d) => {
  const n = daysUntil(d);
  if (n < 0) return { text: `${Math.abs(n)}d overdue`, urgent: true };
  if (n === 0) return { text: 'Today', urgent: true };
  if (n === 1) return { text: 'Tomorrow', urgent: false };
  return { text: `in ${n}d`, urgent: false };
};
const statusColor = { green: '#22C55E', yellow: '#EAB308', red: '#EF4444' };
const statusBg = { green: '#F0FDF4', yellow: '#FEFCE8', red: '#FEF2F2' };
const statusBorder = { green: '#BBF7D0', yellow: '#FDE68A', red: '#FECACA' };
const overallReadiness = (r) => {
  const v = Object.values(r);
  if (v.some((x) => x.status === 'red')) return 'red';
  if (v.some((x) => x.status === 'yellow')) return 'yellow';
  return 'green';
};

// ─── BADGE ───
const Badge = ({ children, variant = 'default', style: sx = {} }) => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    lineHeight: '20px',
    padding: '0 7px',
    borderRadius: 4,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    fontFamily: "'IBM Plex Mono', monospace",
  };
  const v = {
    default: { background: '#F4F4F5', color: '#3F3F46', border: '1px solid #E4E4E7' },
    outline: { background: 'transparent', color: '#52525B', border: '1px solid #D4D4D8' },
    critical: { background: '#18181B', color: '#FAFAFA', border: '1px solid #18181B' },
    flagged: { background: '#FFF', color: '#18181B', border: '1px solid #18181B', fontWeight: 600 },
    edge: { background: '#FFF', color: '#52525B', border: '1px dashed #A1A1AA' },
    green: { background: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' },
    yellow: { background: '#FEFCE8', color: '#854D0E', border: '1px solid #FDE68A' },
    red: { background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' },
  };
  return <span style={{ ...base, ...(v[variant] || v.default), ...sx }}>{children}</span>;
};
const StatusDot = ({ status, size = 8 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: statusColor[status],
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

const AthenaLink = ({ patient }) => (
  <a
    href={athenaUrl(patient.athenaId)}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 10px',
      background: '#FAFAFA',
      border: '1px solid #E4E4E7',
      borderRadius: 6,
      textDecoration: 'none',
      color: '#18181B',
      cursor: 'pointer',
      transition: 'border-color 0.15s',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#A1A1AA')}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E4E4E7')}
  >
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: 4,
        background: '#18181B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: '#FAFAFA',
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        A
      </span>
    </div>
    <div style={{ flex: 1 }}>
      <span style={{ fontSize: 11, fontWeight: 600 }}>Open in Athena</span>
      <span
        style={{
          fontSize: 10,
          color: '#A1A1AA',
          marginLeft: 6,
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        {patient.mrn}
      </span>
    </div>
    <span style={{ fontSize: 13, color: '#A1A1AA' }}>↗</span>
  </a>
);

// ─── TASK ROW ───
const TaskRow = ({ task, compact, onSelect, selected }) => {
  const p = getPatient(task.patientId);
  const due = formatDue(task.due);
  return (
    <div
      onClick={() => onSelect?.(task)}
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? '14px 1fr 62px' : '14px 1fr 90px 78px 62px',
        alignItems: 'center',
        gap: compact ? 6 : 10,
        padding: compact ? '8px 12px' : '10px 14px',
        cursor: 'pointer',
        borderBottom: '1px solid #F4F4F5',
        background: selected ? '#F4F4F5' : '#FFF',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = '#FAFAFA';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = selected ? '#F4F4F5' : '#FFF';
      }}
    >
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          flexShrink: 0,
          border:
            task.priority === 'critical'
              ? '2px solid #18181B'
              : task.status === 'blocked' || task.status === 'flagged'
                ? '2px solid #71717A'
                : '1.5px solid #D4D4D8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 8,
          color: '#71717A',
        }}
      >
        {task.status === 'done' && '✓'}
      </div>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              fontSize: compact ? 12 : 13,
              fontWeight: 500,
              color: '#18181B',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.title}
          </span>
          {task.mustHave2026 && (
            <Badge
              variant="edge"
              style={{ fontSize: 9, lineHeight: '16px', padding: '0 4px', flexShrink: 0 }}
            >
              EDGE
            </Badge>
          )}
        </div>
        {task.caption && (
          <div
            style={{
              fontSize: 11,
              color: '#71717A',
              lineHeight: 1.4,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: compact ? 'nowrap' : 'normal',
              display: compact ? 'block' : '-webkit-box',
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {task.caption}
          </div>
        )}
        {!compact && (
          <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 2 }}>
            {p?.name} → {task.owner}
          </div>
        )}
      </div>
      {!compact && (
        <>
          <div>
            {task.status === 'blocked' ? (
              <Badge variant="critical">Blocked</Badge>
            ) : task.status === 'flagged' ? (
              <Badge variant="flagged">Flagged</Badge>
            ) : (
              <Badge>{task.status}</Badge>
            )}
          </div>
          <Badge variant="outline">{pathwayLabel[task.pathway]}</Badge>
        </>
      )}
      <span
        style={{
          fontSize: 11,
          fontWeight: due.urgent ? 600 : 400,
          color: due.urgent ? '#18181B' : '#A1A1AA',
          fontFamily: "'IBM Plex Mono', monospace",
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {due.text}
      </span>
    </div>
  );
};

// ─── TASK DETAIL PANEL ───
const TaskDetail = ({ task, onClose }) => {
  if (!task) return null;
  const p = getPatient(task.patientId);
  const tm = teamMeta[task.team];
  const due = formatDue(task.due);
  const et = EVIDENCE_TYPES[task.evidence.type];
  return (
    <div
      style={{
        background: '#FFF',
        border: '1px solid #E4E4E7',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 4,
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: '#18181B',
              lineHeight: 1.35,
              paddingRight: 8,
            }}
          >
            {task.title}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 16,
              color: '#A1A1AA',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {task.caption && (
          <div style={{ fontSize: 13, color: '#52525B', lineHeight: 1.5, marginBottom: 8 }}>
            {task.caption}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {task.status === 'blocked' ? (
            <Badge variant="critical">Blocked</Badge>
          ) : task.status === 'flagged' ? (
            <Badge variant="flagged">Flagged</Badge>
          ) : (
            <Badge>{task.status}</Badge>
          )}
          <Badge variant="outline">{pathwayLabel[task.pathway]}</Badge>
          <Badge variant="outline">{task.priority}</Badge>
          {task.mustHave2026 && <Badge variant="edge">2026 Target</Badge>}
        </div>
      </div>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: '5px 0', fontSize: 12 }}
        >
          {[
            ['Patient', `${p?.name} · ${p?.mrn}`],
            ['Assigned to', `${task.owner} (${task.role})`],
            ['Team', `${tm.icon} ${tm.label}`],
            ['Due', `${task.due}  ${due.text}`],
            ['Clinician', p?.clinician],
          ].map(([k, v], i) => (
            <div key={i} style={{ display: 'contents' }}>
              <span style={{ color: '#A1A1AA', fontWeight: 500 }}>{k}</span>
              <span style={{ color: '#3F3F46' }}>{v}</span>
            </div>
          ))}
        </div>
        {p && (
          <div style={{ marginTop: 8 }}>
            <AthenaLink patient={p} />
          </div>
        )}
        {task.noteUrl && (
          <a
            href={task.noteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              marginTop: 6,
              background: '#FEFCE8',
              border: '1px solid #FDE68A',
              borderRadius: 6,
              textDecoration: 'none',
              color: '#18181B',
              cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#EAB308')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#FDE68A')}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                background: '#854D0E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#FEFCE8',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                N
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>View Signed Visit Note</span>
              <span style={{ fontSize: 10, color: '#854D0E', marginLeft: 6 }}>
                99205 · {p?.name}
              </span>
            </div>
            <span style={{ fontSize: 13, color: '#854D0E' }}>↗</span>
          </a>
        )}
      </div>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#71717A',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 6,
          }}
        >
          Why this task exists
        </div>
        <div
          style={{
            background: '#FAFAFA',
            border: '1px solid #E4E4E7',
            borderRadius: 6,
            padding: '10px 12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <span
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600 }}
            >
              {et?.icon}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#52525B',
              }}
            >
              {et?.label}
            </span>
            <span style={{ flex: 1 }} />
            <span
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: '#A1A1AA' }}
            >
              {task.evidence.sourceRef}
            </span>
          </div>
          <p style={{ fontSize: 12, lineHeight: 1.6, color: '#3F3F46', margin: '0 0 6px' }}>
            {task.evidence.summary}
          </p>
          <div style={{ fontSize: 10, color: '#A1A1AA' }}>Source: {task.evidence.source}</div>
        </div>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', gap: 6 }}>
        <button
          style={{
            flex: 1,
            padding: '8px 0',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: 'none',
            background: '#18181B',
            color: '#FAFAFA',
            cursor: 'pointer',
          }}
        >
          Mark Complete
        </button>
        <button
          style={{
            padding: '8px 14px',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: '1px solid #D4D4D8',
            background: '#FFF',
            color: '#3F3F46',
            cursor: 'pointer',
          }}
        >
          Reassign
        </button>
      </div>
    </div>
  );
};

// ─── APPOINTMENT CARD ───
const AppointmentCard = ({ appt, onClick, isSelected }) => {
  const p = getPatient(appt.patientId);
  const vt = VISIT_TYPES[appt.visitType];
  const overall = overallReadiness(appt.readiness);
  const dims = Object.entries(appt.readiness);
  const dn = daysUntil(appt.date);
  const dateLabel =
    dn === 0 ? 'Today' : dn === 1 ? 'Tomorrow' : dn < 0 ? `${Math.abs(dn)}d ago` : `in ${dn}d`;
  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? '#FAFAFA' : '#FFF',
        border: `1.5px solid ${isSelected ? '#18181B' : statusBorder[overall]}`,
        borderRadius: 8,
        padding: '12px 14px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        borderLeft: `4px solid ${statusColor[overall]}`,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.borderColor = '#A1A1AA';
      }}
      onMouseLeave={(e) => {
        if (!isSelected)
          e.currentTarget.style.borderColor = isSelected ? '#18181B' : statusBorder[overall];
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 6,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#18181B' }}>{p?.name}</div>
          <div style={{ fontSize: 11, color: '#52525B', marginTop: 1 }}>
            {vt?.label || appt.visitType}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: dn <= 2 ? '#18181B' : '#71717A',
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            {dateLabel}
          </div>
          <div style={{ fontSize: 10, color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
            {appt.time}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {dims.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              background: statusBg[v.status],
              border: `1px solid ${statusBorder[v.status]}`,
              borderRadius: 4,
              padding: '2px 6px',
            }}
          >
            <StatusDot status={v.status} size={6} />
            <span style={{ fontSize: 10, fontWeight: 500, color: '#52525B' }}>
              {READINESS_DIMS[k]?.label || k}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── READINESS DETAIL PANEL ───
const ReadinessDetail = ({ appt, onClose, onSelectTask }) => {
  if (!appt) return null;
  const p = getPatient(appt.patientId);
  const vt = VISIT_TYPES[appt.visitType];
  const overall = overallReadiness(appt.readiness);
  const dims = Object.entries(appt.readiness);
  const dn = daysUntil(appt.date);
  const gateLabel =
    dn <= 2 ? 'T-2 (final check)' : dn <= 7 ? 'T-7 (action needed)' : 'T-14+ (on track)';
  return (
    <div
      style={{
        background: '#FFF',
        border: '1px solid #E4E4E7',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 4,
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#18181B' }}>{p?.name}</div>
            <div style={{ fontSize: 12, color: '#52525B', marginTop: 2 }}>{vt?.label}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 16,
              color: '#A1A1AA',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <Badge variant={overall}>
            {overall === 'green' ? 'READY' : overall === 'yellow' ? 'AT RISK' : 'NOT READY'}
          </Badge>
          <Badge variant="outline">
            {appt.date} · {appt.time}
          </Badge>
          <Badge variant="outline">{gateLabel}</Badge>
        </div>
        {vt?.description && (
          <div style={{ fontSize: 12, color: '#71717A', lineHeight: 1.5 }}>{vt.description}</div>
        )}
      </div>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '72px 1fr',
            gap: '4px 0',
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          {[
            ['Clinician', appt.clinician],
            ['Location', appt.location],
            ['Pathway', pathwayLabel[p?.pathway]],
            ['Phase', p?.phase],
          ].map(([k, v], i) => (
            <div key={i} style={{ display: 'contents' }}>
              <span style={{ color: '#A1A1AA', fontWeight: 500 }}>{k}</span>
              <span style={{ color: '#3F3F46' }}>{v}</span>
            </div>
          ))}
        </div>
        {p && <AthenaLink patient={p} />}
        {appt.noteUrl && (
          <a
            href={appt.noteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              marginTop: 6,
              background: '#FEFCE8',
              border: '1px solid #FDE68A',
              borderRadius: 6,
              textDecoration: 'none',
              color: '#18181B',
              cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#EAB308')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#FDE68A')}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                background: '#854D0E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#FEFCE8',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                N
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>View Signed Visit Note</span>
              <span style={{ fontSize: 10, color: '#854D0E', marginLeft: 6 }}>
                99205 · {appt.date}
              </span>
            </div>
            <span style={{ fontSize: 13, color: '#854D0E' }}>↗</span>
          </a>
        )}
      </div>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F4F4F5' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#71717A',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 8,
          }}
        >
          What needs to be true for this visit
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dims.map(([k, v]) => {
            const dim = READINESS_DIMS[k];
            const linkedTasks = (v.tasks || []).map(getTask).filter(Boolean);
            return (
              <div
                key={k}
                style={{
                  background: statusBg[v.status],
                  border: `1px solid ${statusBorder[v.status]}`,
                  borderRadius: 6,
                  padding: '10px 12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <StatusDot status={v.status} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#18181B' }}>
                    {dim?.label || k}
                  </span>
                  <span style={{ flex: 1 }} />
                  <Badge variant={v.status}>
                    {v.status === 'green'
                      ? 'Ready'
                      : v.status === 'yellow'
                        ? 'At risk'
                        : 'Not ready'}
                  </Badge>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#3F3F46',
                    lineHeight: 1.5,
                    marginBottom: linkedTasks.length > 0 ? 8 : 0,
                  }}
                >
                  {v.detail}
                </div>
                {linkedTasks.length > 0 && (
                  <div
                    style={{
                      background: '#FFF',
                      border: '1px solid #E4E4E7',
                      borderRadius: 5,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '5px 10px',
                        borderBottom: '1px solid #F4F4F5',
                        background: '#FAFAFA',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#71717A',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        To do
                      </span>
                    </div>
                    {linkedTasks.map((t) => (
                      <TaskRow key={t.id} task={t} compact onSelect={onSelectTask} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <a
            href={athenaUrl(p?.athenaId)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flex: 1,
              padding: '8px 0',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: '1px solid #D4D4D8',
              background: '#FFF',
              color: '#3F3F46',
              cursor: 'pointer',
              textDecoration: 'none',
              textAlign: 'center',
              display: 'block',
            }}
          >
            Reschedule in Athena ↗
          </a>
          <button
            style={{
              flex: 1,
              padding: '8px 0',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              background: '#18181B',
              color: '#FAFAFA',
              cursor: 'pointer',
            }}
          >
            {overall === 'red' ? 'Escalate' : 'Confirm Ready'}
          </button>
        </div>
        {overall === 'red' && dn <= 2 && (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 6,
              padding: '8px 10px',
              fontSize: 12,
              color: '#991B1B',
              lineHeight: 1.5,
            }}
          >
            <span style={{ fontWeight: 600 }}>Final check alert:</span> This visit has unresolved
            blockers within 48 hours. Consider rescheduling or escalating to the clinician.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN APP ───
export default function MindspanCareOrchestration() {
  const [view, setView] = useState('readiness');
  const [activeTeam, setActiveTeam] = useState('all');
  const [pathwayFilter, setPathwayFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [taskFromReadiness, setTaskFromReadiness] = useState(null);

  const sortedAppts = useMemo(
    () => [...APPOINTMENTS].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [],
  );
  const apptsByDate = useMemo(() => {
    const m = {};
    sortedAppts.forEach((a) => {
      (m[a.date] = m[a.date] || []).push(a);
    });
    return m;
  }, [sortedAppts]);
  const readinessStats = useMemo(() => {
    const r = { green: 0, yellow: 0, red: 0 };
    APPOINTMENTS.forEach((a) => {
      r[overallReadiness(a.readiness)]++;
    });
    return r;
  }, []);

  const filtered = useMemo(
    () =>
      TASKS.filter((t) => {
        if (activeTeam !== 'all' && t.team !== activeTeam) return false;
        if (pathwayFilter !== 'all' && t.pathway !== pathwayFilter) return false;
        if (statusFilter === 'open' && t.status === 'done') return false;
        if (statusFilter === 'critical' && t.priority !== 'critical') return false;
        if (statusFilter === 'blocked' && t.status !== 'blocked' && t.status !== 'flagged')
          return false;
        return true;
      }).sort((a, b) => {
        const p = { critical: 0, high: 1, medium: 2, low: 3 };
        return daysUntil(a.due) - daysUntil(b.due) || p[a.priority] - p[b.priority];
      }),
    [activeTeam, pathwayFilter, statusFilter],
  );

  const stats = useMemo(
    () => ({
      total: TASKS.filter((t) => t.status !== 'done').length,
      member: TASKS.filter((t) => t.team === 'member' && t.status !== 'done').length,
      ops: TASKS.filter((t) => t.team === 'ops' && t.status !== 'done').length,
      clinical: TASKS.filter((t) => t.team === 'clinical' && t.status !== 'done').length,
      critical: TASKS.filter((t) => t.priority === 'critical' && t.status !== 'done').length,
      blocked: TASKS.filter((t) => t.status === 'blocked' || t.status === 'flagged').length,
    }),
    [],
  );

  const dayName = (d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d).getDay()];
  const monthDay = (d) => {
    const dt = new Date(d);
    return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()]} ${dt.getDate()}`;
  };

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
        background: '#FAFAFA',
        minHeight: '100vh',
        color: '#18181B',
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: #D4D4D8; border-radius: 3px; }`}</style>

      {/* HEADER */}
      <div style={{ background: '#FFF', borderBottom: '1px solid #E4E4E7' }}>
        <div
          style={{
            maxWidth: 1400,
            margin: '0 auto',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: '#18181B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#FAFAFA',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                M
              </span>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Care Orchestration</div>
              <div style={{ fontSize: 11, color: '#A1A1AA' }}>Post-99205 → Phase 2 Readiness</div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 1,
              background: '#F4F4F5',
              borderRadius: 6,
              padding: 2,
              border: '1px solid #E4E4E7',
            }}
          >
            {[
              {
                k: 'readiness',
                l: 'Readiness',
                badge: readinessStats.red > 0 ? `${readinessStats.red} not ready` : null,
              },
              { k: 'tasks', l: 'Tasks', count: stats.total },
            ].map((v) => (
              <button
                key={v.k}
                onClick={() => {
                  setView(v.k);
                  setSelectedTask(null);
                  setSelectedAppt(null);
                  setTaskFromReadiness(null);
                }}
                style={{
                  padding: '6px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  background: view === v.k ? '#FFF' : 'transparent',
                  color: view === v.k ? '#18181B' : '#71717A',
                  boxShadow: view === v.k ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {v.l}
                {v.count != null && (
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: '#A1A1AA',
                    }}
                  >
                    {v.count}
                  </span>
                )}
                {v.badge && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      background: '#FEF2F2',
                      color: '#991B1B',
                      padding: '1px 5px',
                      borderRadius: 3,
                      border: '1px solid #FECACA',
                    }}
                  >
                    {v.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#A1A1AA' }}
            >
              {SIM_TODAY}
            </span>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#F4F4F5',
                border: '1px solid #E4E4E7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                color: '#52525B',
              }}
            >
              AR
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 20px' }}>
        {/* READINESS VIEW */}
        {view === 'readiness' && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Upcoming 99483s', value: APPOINTMENTS.length, sub: 'visits' },
                { label: 'Ready', value: readinessStats.green, color: 'green' },
                { label: 'At Risk', value: readinessStats.yellow, color: 'yellow' },
                { label: 'Not Ready', value: readinessStats.red, color: 'red' },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: '#FFF',
                    border: '1px solid #E4E4E7',
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#71717A', fontWeight: 500, marginBottom: 2 }}>
                    {s.label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: s.color ? statusColor[s.color] : '#18181B',
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}
                    >
                      {s.value}
                    </span>
                    {s.sub && <span style={{ fontSize: 11, color: '#A1A1AA' }}>{s.sub}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: selectedAppt || taskFromReadiness ? '1fr 380px' : '1fr',
                gap: 16,
                alignItems: 'start',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {Object.entries(apptsByDate).map(([date, appts]) => {
                  const dn = daysUntil(date);
                  const isToday = dn === 0;
                  return (
                    <div key={date}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
                      >
                        <div style={{ width: 40, textAlign: 'center' }}>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: isToday ? '#18181B' : '#A1A1AA',
                              textTransform: 'uppercase',
                            }}
                          >
                            {dayName(date)}
                          </div>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: isToday ? '#18181B' : '#52525B',
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {new Date(date).getDate()}
                          </div>
                        </div>
                        <div style={{ flex: 1, height: 1, background: '#E4E4E7' }} />
                        <span style={{ fontSize: 11, color: '#A1A1AA' }}>
                          {monthDay(date)}
                          {isToday ? ' · Today' : ''}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                          gap: 8,
                          paddingLeft: 48,
                        }}
                      >
                        {appts.map((a) => (
                          <AppointmentCard
                            key={a.id}
                            appt={a}
                            isSelected={selectedAppt?.id === a.id}
                            onClick={() => {
                              setSelectedAppt(a);
                              setTaskFromReadiness(null);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {(selectedAppt || taskFromReadiness) && (
                <div style={{ position: 'sticky', top: 16 }}>
                  {taskFromReadiness ? (
                    <TaskDetail
                      task={taskFromReadiness}
                      onClose={() => setTaskFromReadiness(null)}
                    />
                  ) : (
                    <ReadinessDetail
                      appt={selectedAppt}
                      onClose={() => setSelectedAppt(null)}
                      onSelectTask={setTaskFromReadiness}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TASKS VIEW */}
        {view === 'tasks' && (
          <>
            <div
              style={{
                background: '#FFF',
                border: '1px solid #E4E4E7',
                borderRadius: 8,
                padding: '8px 14px',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 1,
                  background: '#F4F4F5',
                  borderRadius: 5,
                  padding: 2,
                }}
              >
                {[
                  { k: 'all', l: 'All', c: stats.total },
                  { k: 'member', l: 'Member', c: stats.member },
                  { k: 'ops', l: 'Ops', c: stats.ops },
                  { k: 'clinical', l: 'Clinical', c: stats.clinical },
                ].map((t) => (
                  <button
                    key={t.k}
                    onClick={() => setActiveTeam(t.k)}
                    style={{
                      padding: '3px 10px',
                      fontSize: 11,
                      fontWeight: 500,
                      borderRadius: 3,
                      border: 'none',
                      cursor: 'pointer',
                      background: activeTeam === t.k ? '#FFF' : 'transparent',
                      color: activeTeam === t.k ? '#18181B' : '#71717A',
                      boxShadow: activeTeam === t.k ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    {t.l}{' '}
                    <span
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        color: '#A1A1AA',
                        marginLeft: 2,
                      }}
                    >
                      {t.c}
                    </span>
                  </button>
                ))}
              </div>
              <div style={{ width: 1, height: 20, background: '#E4E4E7' }} />
              <div
                style={{
                  display: 'flex',
                  gap: 1,
                  background: '#F4F4F5',
                  borderRadius: 5,
                  padding: 2,
                }}
              >
                {[
                  { k: 'open', l: 'Open' },
                  { k: 'critical', l: `Critical ${stats.critical}` },
                  { k: 'blocked', l: `Blocked ${stats.blocked}` },
                ].map((s) => (
                  <button
                    key={s.k}
                    onClick={() => setStatusFilter(s.k)}
                    style={{
                      padding: '3px 9px',
                      fontSize: 11,
                      fontWeight: 500,
                      borderRadius: 3,
                      border: 'none',
                      cursor: 'pointer',
                      background: statusFilter === s.k ? '#FFF' : 'transparent',
                      color: statusFilter === s.k ? '#18181B' : '#71717A',
                      boxShadow: statusFilter === s.k ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: selectedTask ? '1fr 380px' : '1fr',
                gap: 16,
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  background: '#FFF',
                  border: '1px solid #E4E4E7',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '14px 1fr 90px 78px 62px',
                    gap: 10,
                    padding: '7px 14px',
                    borderBottom: '1px solid #E4E4E7',
                    background: '#FAFAFA',
                  }}
                >
                  <span />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#71717A',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Task
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#71717A',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Status
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#71717A',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Pathway
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#71717A',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      textAlign: 'right',
                    }}
                  >
                    Due
                  </span>
                </div>
                {activeTeam === 'all'
                  ? ['member', 'ops', 'clinical'].map((tk) => {
                      const tt = filtered.filter((t) => t.team === tk);
                      if (!tt.length) return null;
                      const tm = teamMeta[tk];
                      return (
                        <div key={tk}>
                          <div
                            style={{
                              padding: '6px 14px',
                              borderBottom: '1px solid #F4F4F5',
                              background: '#FAFAFA',
                            }}
                          >
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#52525B' }}>
                              {tm.icon} {tm.label}
                            </span>
                            <span style={{ fontSize: 11, color: '#A1A1AA', marginLeft: 6 }}>
                              {tt.length}
                            </span>
                          </div>
                          {tt.map((t) => (
                            <TaskRow
                              key={t.id}
                              task={t}
                              selected={selectedTask?.id === t.id}
                              onSelect={setSelectedTask}
                            />
                          ))}
                        </div>
                      );
                    })
                  : filtered.map((t) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        selected={selectedTask?.id === t.id}
                        onSelect={setSelectedTask}
                      />
                    ))}
                {!filtered.length && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#A1A1AA', fontSize: 13 }}>
                    No tasks match filters
                  </div>
                )}
              </div>
              {selectedTask && (
                <div style={{ position: 'sticky', top: 16 }}>
                  <TaskDetail task={selectedTask} onClose={() => setSelectedTask(null)} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
